import CheckCircle from "@mui/icons-material/CheckCircle";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import LanguageOutlined from "@mui/icons-material/LanguageOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Typography,
  type ChipProps,
} from "@mui/material";
import { useEffect, useId, useRef, useState, type FC, type ReactNode } from "react";
import { AdditionalServicesService } from "../../modules/additional-services/additional-services-service";
import { useClickwrapTerms } from "../../modules/clickwrap/use-clickwrap-terms";
import { ExosIcon } from "../../modules/exos/exos-icon";
import { RegisterInterestDialog } from "../../modules/exos/register-interest-dialog";
import { HeaderBar } from "../../modules/generic/components/header-bar";
import { Page } from "../../modules/layout/components/page";
import { useFlags } from "../../server/optimizely";
import { useSupabaseCallback } from "../../server/supabase/hooks";
import NotFound from "../not-found";

const gradientBackground = "radial-gradient(96.78% 141.42% at 0% 0%, #582F79 0%, #231335 100%)";
const bannerImage800 =
  "https://images.changeengine.com/b89bd028-0199-47a5-8276-eb4b17cd4d73/2f32c53a-2d1c-4556-bdf0-b327b13e0cf9";
const bannerImage1600 =
  "https://images.changeengine.com/b89bd028-0199-47a5-8276-eb4b17cd4d73/8987431d-8dc2-4d74-bbb3-f76efe2725db";

const GradientLanguageIcon: FC = () => {
  const gradientId = useId();

  return (
    <>
      <svg width="0" height="0" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00B6EF" />
            <stop offset="100%" stopColor="#C24599" />
          </linearGradient>
        </defs>
      </svg>
      <LanguageOutlined sx={{ fill: `url(#${gradientId})`, fontSize: 60, mb: 2.5 }} />
    </>
  );
};

interface OptionCardProps {
  badgeText: string;
  badgeColor: ChipProps["color"];
  title: string;
  description: ReactNode;
  points: string[];
}

const OptionCard: FC<OptionCardProps> = ({ badgeText, badgeColor, title, description, points }) => (
  <Box
    component="article"
    sx={{
      flex: 1,
      minWidth: 0,
      p: { xs: 2, sm: 2.5 },
      border: "1px solid transparent",
      borderRadius: 1,
      background: ({ palette }) =>
        `linear-gradient(${palette.background.paper}, ${palette.background.paper}) padding-box, linear-gradient(135deg, #38BDF8, #F472B6) border-box`,
      boxShadow: "0 4px 10px rgba(17, 24, 39, 0.08)",
    }}
  >
    <Chip
      label={badgeText}
      size="small"
      color={badgeColor}
      sx={{
        height: 24,
        mb: 1.5,
        fontSize: 12,
        fontWeight: 700,
        "& .MuiChip-label": { px: 1.25 },
      }}
    />
    <Typography variant="h5" sx={{ mb: 1.5 }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ mb: 2, color: "text.primary" }}>
      {description}
    </Typography>
    <Stack component="ul" spacing={1.25} sx={{ p: 0, m: 0, listStyle: "none" }}>
      {points.map((point) => (
        <Stack component="li" key={point} direction="row" spacing={1.25} alignItems="center">
          <CheckCircle sx={{ flex: "0 0 auto", color: "#10B981", fontSize: 20 }} />
          <Typography variant="body2">{point}</Typography>
        </Stack>
      ))}
    </Stack>
  </Box>
);

interface ExistingCustomerActionProps {
  hasAcceptedCurrentVersion: boolean;
  button: ReactNode;
}

const ExistingCustomerAction: FC<ExistingCustomerActionProps> = ({
  hasAcceptedCurrentVersion,
  button,
}) =>
  hasAcceptedCurrentVersion ? (
    <Alert severity="success">Thanks for registering - we’ll be in touch soon.</Alert>
  ) : (
    <>{button}</>
  );

const ExosPage: FC = () => {
  const existingCustomerSectionRef = useRef<HTMLElement>(null);
  const [registerInterestModalOpen, setRegisterInterestModalOpen] = useState(false);
  const openRegisterInterestModal = (): void => setRegisterInterestModalOpen(true);
  const {
    flags: { ["cha-4488_exos"]: exos },
    flagsLoaded,
  } = useFlags("cha-4488_exos");

  const clickwrapTerms = useClickwrapTerms("exos");
  const acceptClickwrapTerms = clickwrapTerms.acceptClickwrapTerms;
  const registerInterest = useSupabaseCallback(async ({ supabase }) => {
    await new AdditionalServicesService(supabase).insert({
      service_type: "exos",
    });
  }, []);

  useEffect(() => {
    if (clickwrapTerms.status !== "idle") return;
    void clickwrapTerms.fetchClickwrapTerms();
  }, [clickwrapTerms]);

  if (!flagsLoaded || clickwrapTerms.status === "loading") return <LinearProgress />;
  if (!exos) return <NotFound />;

  return (
    <Page
      title="EXOS | ChangeEngine"
      data-testid="exos-page"
      sx={{
        flex: "1 1 auto",
        minHeight: 0,
        overflowX: "hidden",
        overflowY: "auto",
        bgcolor: "common.white",
      }}
    >
      <HeaderBar navBurger>
        <Typography
          variant="body1"
          fontWeight={700}
          fontSize={16}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <ExosIcon />
          Employee Experience Operating System
        </Typography>
      </HeaderBar>

      <Box component="main">
        <Box
          component="section"
          sx={{
            color: "common.white",
            background: gradientBackground,
            px: { xs: 2.5, sm: 5, md: 8 },
            pt: { xs: 6, sm: 8, md: 10 },
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 800, mx: "auto", textAlign: "center" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1.5, sm: 2 }}
              alignItems="center"
              justifyContent="center"
              sx={{ mb: 3 }}
            >
              <ExosIcon fontSize={{ xs: 40, sm: 44 }} />
              <Typography variant="h3" sx={{ color: "inherit" }}>
                Employee experiences, made better.
              </Typography>
            </Stack>

            <Typography variant="body2" sx={{ color: "inherit", mb: 2.5 }}>
              Create branded microsites for every stage of the employee experience, from onboarding
              and recognition to rewards and career development.
            </Typography>
            <Typography variant="body2" sx={{ color: "inherit", mb: 3 }}>
              EXOS gives HR teams a library of ready-to-use microsites, personalized with your
              company’s branding and ready for you to edit and make your own.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 1.5 }}
              alignItems="center"
              justifyContent="center"
            >
              <ExistingCustomerAction
                hasAcceptedCurrentVersion={clickwrapTerms.hasAcceptedCurrentVersion}
                button={
                  <>
                    <Button
                      type="button"
                      variant="contained"
                      data-analytics-id="exos-hero-register-interest"
                      onClick={openRegisterInterestModal}
                    >
                      Register your Interest
                    </Button>
                    <Button
                      type="button"
                      variant="text"
                      data-analytics-id="exos-hero-existing-customer"
                      onClick={() =>
                        existingCustomerSectionRef.current?.scrollIntoView({ behavior: "smooth" })
                      }
                      endIcon={<KeyboardArrowDown />}
                      sx={{ color: "inherit" }}
                    >
                      Already using Pages or FDE?
                    </Button>
                  </>
                }
              />
            </Stack>
          </Box>

          <Box
            sx={{
              height: { xs: 150, sm: 190, md: 220 },
              maxWidth: 800,
              mx: "auto",
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <Box
              component="img"
              src={bannerImage800}
              srcSet={`${bannerImage800} 800w, ${bannerImage1600} 1600w`}
              sizes="(max-width: 600px) calc(100vw - 40px), 800px"
              alt="Examples of branded employee experience microsites"
              width={800}
              height={184}
              sx={{ display: "block", width: "100%", height: "auto" }}
            />
          </Box>
        </Box>

        <Box
          component="section"
          aria-label="Ways to get started"
          sx={{
            backgroundColor: "background.default",
            px: { xs: 2, sm: 4, md: 6 },
            py: { xs: 4, md: 5 },
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2.5}
            sx={{ width: "100%", maxWidth: 1320, mx: "auto" }}
          >
            <OptionCard
              badgeText="SELF-SERVE"
              badgeColor="primary"
              title="Build your own prototype"
              description={
                <>
                  Pick any program, we instantly brand it with your company’s logo and colors, and
                  you get a live site in minutes. Free to start, no credit card.
                </>
              }
              points={[
                "50+ ready-to-launch programs",
                "Auto-branded to your company",
                "Prototype ready in minutes for our team to review",
              ]}
            />
            <OptionCard
              badgeText="WHITE GLOVE"
              badgeColor="success"
              title="Have an engineer build it with you"
              description={
                <>
                  Want something bespoke? A ChangeEngine Forward Deployed Engineer embeds with your
                  People team and ships custom internal products wired to your HRIS and ATS data, at
                  a pace vendors can’t touch.
                </>
              }
              points={[
                "Dedicated engineer on your team",
                "Wired to your HRIS / ATS data",
                "First prototype in 48 hours",
              ]}
            />
          </Stack>

          <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 3, md: 4 } }}>
            <ExistingCustomerAction
              hasAcceptedCurrentVersion={clickwrapTerms.hasAcceptedCurrentVersion}
              button={
                <Button
                  type="button"
                  variant="contained"
                  data-analytics-id="exos-options-register-interest"
                  onClick={openRegisterInterestModal}
                >
                  Register your Interest
                </Button>
              }
            />
          </Box>
        </Box>
      </Box>

      <Box
        component="footer"
        ref={existingCustomerSectionRef}
        sx={{
          minHeight: { xs: 360, md: 440 },
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          color: "common.white",
          background: gradientBackground,
          px: { xs: 2.5, sm: 5 },
          py: { xs: 6, md: 7 },
        }}
      >
        <Stack alignItems="center" sx={{ width: "100%", maxWidth: 780, textAlign: "center" }}>
          <GradientLanguageIcon />
          <Typography component="h2" variant="h5" sx={{ color: "inherit", mb: 2.5 }}>
            Already using Pages or FDE?
          </Typography>
          <Typography variant="body2" sx={{ color: "inherit", mb: 3 }}>
            EXOS brings Pages and our FDE service together into one more powerful employee
            experience platform. If you’re already using Pages or FDE, review and accept the Service
            Terms.
          </Typography>
          <ExistingCustomerAction
            hasAcceptedCurrentVersion={clickwrapTerms.hasAcceptedCurrentVersion}
            button={
              <Button
                type="button"
                variant="contained"
                data-analytics-id="exos-footer-service-terms"
                onClick={openRegisterInterestModal}
              >
                Review and accept the service Terms
              </Button>
            }
          />
        </Stack>
      </Box>

      <RegisterInterestDialog
        open={registerInterestModalOpen}
        onClose={() => setRegisterInterestModalOpen(false)}
        onAccept={() => {
          void acceptClickwrapTerms();
          void registerInterest();
        }}
        clickwrapTerms={clickwrapTerms}
      />
    </Page>
  );
};

export default ExosPage;
